// ─────────────────────────────────────────────────────────────────────────────
// «نوبت‌دهی آنلاین» — the shared, render-free half (PM-0382,
// docs/DOCTOR_PAGE_INSTANT_BOOKING_PLAN.md §4.6, owner D6 / D7).
//
// What a typed horizon means, what makes a day off valid, the variables each
// mutation is sent with, and how a day off is worded. Everything here is a pure
// function, so the rules are tests and the surfaces only render them. The BFF
// re-checks every rule (parsina.bff.web src/lib/onlineBooking.js); these are the
// doctor's sentence BEFORE the round trip, not the authority.
//
// Dates are the CLINIC's calendar days, "YYYY-MM-DD" — the same value the kit's
// JalaliDatePicker emits — and «امروز» is the clinic's, as the BFF reports it
// (`OnlineBookingSetting.today`). Nothing here reads the browser's clock.
// ─────────────────────────────────────────────────────────────────────────────

// «تا [ N ] روز آینده»: 1–365, empty = the clinic's own horizon.
export const HORIZON_MIN = 1;
export const HORIZON_MAX = 365;

// scheduling's limits for one day off (CreatePresenceExceptionRequest).
export const DAY_OFF_MAX_DAYS = 366;
export const DAY_OFF_REASON_MAX = 200;
export const MINUTES_PER_DAY = 1440;

// The partial-day picker's step. Presence hours are set on quarter hours.
export const MINUTE_STEP = 15;

// A new partial day starts on a plausible morning, not at midnight.
const DEFAULT_PART = { start: 8 * 60, end: 12 * 60 };

const DAY_MS = 24 * 60 * 60 * 1000;

// Persian / Arabic-Indic digits → ASCII, so «۱۴» is 14 (§16.1: storage and wire
// are ASCII). The kit inputs normalise already; this keeps the rule total.
const asciiDigits = (s) => String(s ?? '')
  .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
  .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660));

// ── The horizon ──────────────────────────────────────────────────────────────

/** The field's text for a stored setting: '' when the clinic's horizon applies. */
export const horizonDraftOf = (setting) => (setting?.horizonDays ? String(setting.horizonDays) : '');

/**
 * The typed horizon → `{ value }` (a whole number of days, or null = the clinic's)
 * or `{ error }` (an i18n key).
 */
export function parseHorizonDraft(draft) {
  const text = asciiDigits(draft).trim();
  if (!text) return { value: null };
  if (!/^\d+$/.test(text)) return { error: 'settings.onlineBooking.horizon.invalid' };
  const n = Number(text);
  if (n < HORIZON_MIN || n > HORIZON_MAX) return { error: 'settings.onlineBooking.horizon.invalid' };
  return { value: n };
}

/** The draft differs from what is stored — comparing what each MEANS, not the keystrokes. */
export function isHorizonDirty(draft, setting) {
  const parsed = parseHorizonDraft(draft);
  if (parsed.error) return true;
  return (parsed.value ?? null) !== (setting?.horizonDays ?? null);
}

/**
 * The line under the field: what self-service uses NOW, as `{ key, vars }`.
 * Paused says so; an own number above the clinic's says the clinic's applies.
 */
export function horizonStatus(setting) {
  if (!setting) return null;
  if (setting.paused) return { key: 'settings.onlineBooking.horizon.paused' };
  const effective = Number(setting.effectiveHorizonDays) || 0;
  if (effective <= 0) return { key: 'settings.onlineBooking.horizon.effectiveNone' };
  if (setting.horizonDays && setting.horizonDays > (Number(setting.clinicHorizonDays) || 0)) {
    return { key: 'settings.onlineBooking.horizon.capped', vars: { days: effective } };
  }
  return { key: 'settings.onlineBooking.horizon.effective', vars: { days: effective } };
}

// ── Days off ─────────────────────────────────────────────────────────────────

export const DAY_OFF_SPANS = Object.freeze(['whole', 'part']);

export const emptyDayOffForm = () => ({
  from: '', to: '', span: 'whole', start: DEFAULT_PART.start, end: DEFAULT_PART.end, reason: '',
});

/** The form holds something worth asking about before it is thrown away. */
export const isDayOffFormDirty = (form) => Boolean(form?.from || form?.to || String(form?.reason || '').trim()
  || form?.span === 'part');

const isCalendarDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

/** Calendar days from `from` to `to`, both counted. */
export const daySpan = (from, to) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

/**
 * Field errors as i18n keys — the same five rules the BFF applies, on the field
 * each is about: a start date; an end not before it; at most 366 days; not ending
 * before the clinic's today; a partial day that starts before it ends.
 */
export function validateDayOff(form, today) {
  const errors = {};
  const from = form?.from || '';
  const to = form?.to || '';
  if (!isCalendarDate(from)) {
    errors.from = 'settings.onlineBooking.dayOff.errors.from';
  } else if (to && !isCalendarDate(to)) {
    // Not reachable from the picker, which emits a real day or ''; said anyway.
    errors.to = 'errors.bff.onlineBooking.dayOffDate';
  } else {
    const end = to || from;
    if (end < from) errors.to = 'settings.onlineBooking.dayOff.errors.order';
    else if (daySpan(from, end) > DAY_OFF_MAX_DAYS) errors.to = 'settings.onlineBooking.dayOff.errors.range';
    else if (today && end < today) errors[to ? 'to' : 'from'] = 'settings.onlineBooking.dayOff.errors.past';
  }
  if (form?.span === 'part') {
    const start = Number(form.start);
    const end = Number(form.end);
    if (!(Number.isInteger(start) && Number.isInteger(end) && start >= 0 && start < end && end <= MINUTES_PER_DAY)) {
      errors.end = 'settings.onlineBooking.dayOff.errors.hours';
    }
  }
  return errors;
}

/** The order a failed submit focuses its fields in (§6.3: the first invalid one). */
export const DAY_OFF_FIELDS = Object.freeze(['from', 'to', 'start', 'end', 'reason']);

/** addMyDayOff's variables. A whole day sends no minutes; an empty end, no end. */
export function dayOffVariables(form, clinicId) {
  const part = form.span === 'part';
  const reason = String(form.reason || '').trim().slice(0, DAY_OFF_REASON_MAX);
  return {
    clinicId,
    dateFrom: form.from,
    dateTo: form.to || null,
    startMinute: part ? Number(form.start) : null,
    endMinute: part ? Number(form.end) : null,
    reason: reason || null,
  };
}

/**
 * Which field a BFF refusal is about (its `extensions.messageKey`), so it lands
 * there rather than in a banner (§6.3). Anything else is the form's own alert.
 */
export const DAY_OFF_FIELD_OF_ERROR = Object.freeze({
  'errors.bff.onlineBooking.dayOffDate': 'from',
  'errors.bff.onlineBooking.dayOffOrder': 'to',
  'errors.bff.onlineBooking.dayOffRange': 'to',
  'errors.bff.onlineBooking.dayOffPast': 'to',
  'errors.bff.onlineBooking.dayOffMinutes': 'end',
});

// ── Times of day ─────────────────────────────────────────────────────────────

/** 570 → "09:30"; 1440 → "24:00" (the end of the day). ASCII: the caller formats digits. */
export const minutesToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * The partial-day picker's choices, as `{ value, label }` with string values.
 * A start can be 00:00–23:45, an end 00:15–24:00. `formatDigits` puts the label
 * in the reader's digits (the caller's `useTranslation().formatDigits`).
 */
export function timeOptions(kind, formatDigits = (s) => s) {
  const out = [];
  const first = kind === 'end' ? MINUTE_STEP : 0;
  const last = kind === 'end' ? MINUTES_PER_DAY : MINUTES_PER_DAY - MINUTE_STEP;
  for (let m = first; m <= last; m += MINUTE_STEP) {
    out.push({ value: String(m), label: formatDigits(minutesToHHMM(m)) });
  }
  return out;
}

// ── Wording a day off ────────────────────────────────────────────────────────

/**
 * A calendar day as an instant the kit's formatters render as THAT day: noon UTC
 * is the same date in every zone within ±11 hours, whichever zone the formatter
 * pins — a clinic's date never slides into the day before.
 */
export const calendarDateInstant = (iso) => new Date(`${iso}T12:00:00Z`);

/** «۲۵ مهر ۱۴۰۵» or «۲۵ مهر ۱۴۰۵ تا ۲۸ مهر ۱۴۰۵». */
export function dayOffRangeLabel(dayOff, { t, formatDate }) {
  const from = formatDate(calendarDateInstant(dayOff.dateFrom), 'medium');
  if (!dayOff.dateTo || dayOff.dateTo === dayOff.dateFrom) return from;
  return t('settings.onlineBooking.daysOff.range', {
    from, to: formatDate(calendarDateInstant(dayOff.dateTo), 'medium'),
  });
}

/** «تمام روز» or «۱۰:۰۰ تا ۱۳:۰۰». */
export function dayOffHoursLabel(dayOff, { t, formatDigits }) {
  if (dayOff.wholeDay || dayOff.startMinute == null || dayOff.endMinute == null) {
    return t('settings.onlineBooking.daysOff.wholeDay');
  }
  return t('settings.onlineBooking.daysOff.hours', {
    start: formatDigits(minutesToHHMM(dayOff.startMinute)),
    end: formatDigits(minutesToHHMM(dayOff.endMinute)),
  });
}
